// WHAT THE WALLET REGISTER SAYS ABOUT A ROW, AND HOW IT ARRANGES THE ROWS — said once, for the laptop's table and the phone's cards, and pure, so
// the words are tested without a renderer. The screen (screens/v2/Wallets.tsx) draws; nothing here decides what a step does.
//
// FROM Screens' Astra-approved reformat (branch screens/wallet-register, 538a2ff — lib/walletRegisterView.ts), carried whole: the views, the
// filters, the payout stage, a row's steps and its form (rounds 1–6). Batch 5 of the UI rebuild ADDS what the operator's ruling of 26 Oct asks of it
// (Astra's ONE register): the sections are flattened into one list in the same order — desk, payout, parties — each row saying its role in words, and
// a Ledger account key's addresses become ONE row per party that opens them (registerRows); a typed address is checked against the register for a
// look-alike (lookalikeIn); and a one-way step says what it does in one line, its full sentence behind a tap (stepLine).
//
// THE ARRANGEMENT. Three sections, in this order, each held to the rows that belong in it and gone when it has none:
//   Desk wallets      — an address of one of the desk's own accounts (it has a Treasury account);
//   Payout addresses  — an address the desk pays (class customer_payout, db/0066), with its approval stage;
//   Parties           — every other address a party was given (the class not yet set).
// Inside a section the rows stand under their party, in the order the register sends them (desk first). An address that follows from a Ledger
// account key is the key's scan's: it is counted, searched and shown, folded under its party, and never changed here.

import type { RegisterWallet } from '../../../lib/api';
import type { PillTone } from '../../Pill';
import { fmtDay, fmtRelative } from '../../../lib/format';
import { WALLET_CHAINS, chainIsRead, walletState } from '../../../lib/walletStates';

/** A row as the Worker sends it once the wallet's db/0098 is applied: a payout address's stage, in the database's words. An older Worker sends none. */
export type StagedWallet = RegisterWallet & { payout_stage?: string | null };

// ── the filters ──────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

export type Show = 'current' | 'retired' | 'archived' | 'all';
/** The four views of the register, in the operator's words (db/0064): the database's 'archived' is his RETIRED, and his archive is 'unwatched'. */
export const SHOWS: ReadonlyArray<{ key: Show; label: string; has: (w: RegisterWallet) => boolean }> = [
  { key: 'current',  label: 'In use',   has: (w) => w.status === 'active' || w.status === 'flagged' },
  { key: 'retired',  label: 'Retired',  has: (w) => w.status === 'archived' },
  { key: 'archived', label: 'Archived', has: (w) => w.status === 'unwatched' },
  { key: 'all',      label: 'All',      has: () => true },
];

export function showCounts(all: readonly RegisterWallet[]): Record<Show, number> {
  return Object.fromEntries(SHOWS.map((s) => [s.key, all.filter(s.has).length])) as Record<Show, number>;
}

/** The register's own name for a wallet (N-091, bff/src/lib/wallet-name.ts): the route sends `name` beside the row as a WalletName
 *  object — `full` is the one line of words ("Hot · TRC20 · Oct"); null while the wallet is not sorted. The shared type predates
 *  it, so it is read here, once, for the row's title and the search. */
export function walletNameOf(w: RegisterWallet): string | null {
  const n = (w as { name?: { full?: unknown } | null }).name;
  return n && typeof n === 'object' && typeof n.full === 'string' && n.full ? n.full : null;
}

/** The name's first line, group and network ("Deposit · TRC20"), or null from an older server. */
const nameLine1Of = (w: RegisterWallet): string | null => {
  const n = (w as { name?: { line1?: unknown } | null }).name;
  return n && typeof n === 'object' && typeof n.line1 === 'string' && n.line1 ? n.line1 : null;
};

/** EVERY NAME ON SCREEN IS THE REGISTER'S (N-091; the CC, 3 Nov 2026 — his banner named #43 by its network and typed label in "… is approved
 *  for payouts"): the name the route sends, and only from an older server without one, the network and the old label. */
export const nameOf = (w: RegisterWallet): string => walletNameOf(w) ?? addressName(w);

/** The name a folded row stands under — a Ledger account key's addresses, or the deposit addresses — never "Ledger account key" or a count:
 *  the one name its addresses share (a BTC key's are ONE wallet), else their one group and network ("Deposit · TRC20"), else the group and
 *  each network. The deposit row always takes the group and network, never one customer's. null from an older server. */
export function foldName(rows: readonly RegisterWallet[], deposits = false): string | null {
  const one = (xs: Array<string | null>) => { const s = new Set(xs); return s.size === 1 && !s.has(null) ? [...s][0] : null; };
  if (rows.length === 0) return null;
  if (!deposits) { const n = one(rows.map(walletNameOf)); if (n) return n; }
  const line1s = rows.map(nameLine1Of);
  const l = one(line1s);
  if (l) return l;
  if (line1s.some((x) => x === null)) return null;
  const group = one(line1s.map((x) => x!.split(' · ')[0] ?? null));
  return group ? [group, ...new Set(line1s.map((x) => x!.split(' · ').slice(1).join(' · ')))].join(' · ') : null;
}

/** "1 address", "3 addresses". */
export const addressCount = (n: number): string => `${n} ${n === 1 ? 'address' : 'addresses'}`;

/** The rows a view, a chain and a search leave: the search looks in the party, the name, the label and the address. A row whose form is OPEN stays in the list
 *  whatever the filters say (`keep`: its id): a request in flight, a failure it answered, a reason half typed all live in that form, and a filter that
 *  unmounted it would lose them (Astra, 26 Oct). */
export function filterRegister(all: readonly RegisterWallet[], f: { show: Show; chain: string; search: string }, keep: Readonly<Record<string, unknown>> = {}): RegisterWallet[] {
  const has = SHOWS.find((s) => s.key === f.show)!.has;
  const needle = f.search.trim().toLowerCase();
  return all.filter((w) => w.id in keep || (has(w) && (!f.chain || w.chain === f.chain) && (!needle
    || w.address.toLowerCase().includes(needle)
    || (w.label ?? '').toLowerCase().includes(needle)
    || (walletNameOf(w) ?? '').toLowerCase().includes(needle)
    || (w.party_name ?? w.party_slug).toLowerCase().includes(needle))));
}

/** The chains the register holds, in the order the add form offers them — and the one chosen, even when the data no longer holds it. */
export function chainsPresent(all: readonly RegisterWallet[], chosen = ''): string[] {
  const held = new Set(all.map((w) => w.chain));
  const out: string[] = WALLET_CHAINS.filter((c) => held.has(c));
  for (const c of held) if (!out.includes(c)) out.push(c);
  if (chosen && !out.includes(chosen)) out.push(chosen);
  return out;
}

// ── the sections ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

export type SectionKey = 'desk' | 'payout' | 'parties';
export const SECTION_TITLE: Record<SectionKey, string> = { desk: 'Desk wallets', payout: 'Payout addresses', parties: 'Parties' };
const SECTION_ORDER: readonly SectionKey[] = ['desk', 'payout', 'parties'];

export const isPayout = (w: RegisterWallet): boolean => w.class === 'customer_payout';
/** An address the desk may pay, so the owner's Approve payout is its: a payout address, or a desk wallet outside custody the
 *  wallet lists as a payout destination (its item 6, 2 Nov 2026) — the server then gives the row a payout stage. */
export const approvesAsPayout = (w: RegisterWallet): boolean => isPayout(w) || (w.payout_stage !== undefined && w.payout_stage !== null);

export function sectionOf(w: RegisterWallet): SectionKey {
  if (isPayout(w)) return 'payout';
  return w.treasury_slug ? 'desk' : 'parties';
}

export interface RegisterGroup {
  /** Unique across sections: a party with a payout address and a plain one stands in two sections. */
  key: string;
  slug: string;
  name: string;
  desk: boolean;
  treasury: string | null;
  type: string;
  byHand: RegisterWallet[];
  fromKey: RegisterWallet[];
}
export interface RegisterSection { key: SectionKey; title: string; count: number; groups: RegisterGroup[]; payout: boolean }

export function registerSections(rows: readonly RegisterWallet[]): RegisterSection[] {
  const by = new Map<SectionKey, RegisterGroup[]>();
  for (const w of rows) {
    const key = sectionOf(w);
    const groups = by.get(key) ?? [];
    by.set(key, groups);
    let g = groups.find((x) => x.slug === w.party_slug);
    if (!g) {
      g = { key: `${key}:${w.party_slug}`, slug: w.party_slug, name: w.party_name ?? w.party_slug, desk: !!w.treasury_slug, treasury: w.treasury_slug, type: w.party_type, byHand: [], fromKey: [] };
      groups.push(g);
    }
    (w.from_ledger_key ? g.fromKey : g.byHand).push(w);
  }
  return SECTION_ORDER.filter((k) => by.has(k)).map((k) => {
    const groups = by.get(k)!;
    return { key: k, title: SECTION_TITLE[k], groups, payout: k === 'payout', count: groups.reduce((n, g) => n + g.byHand.length + g.fromKey.length, 0) };
  });
}

// ── a row's words ────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** "TRC20 · Office hot" — what a notice and a tooltip call an address. */
export const addressName = (w: RegisterWallet): string => `${w.chain}${w.label ? ` · ${w.label}` : ''}`;

export interface PayoutFacts { word: string; tone: PillTone; when: string | null; hint: string | null }

/** The one stage word of a payout address — Approved → Test sent → Whitelisted (the operator's rule of 24 Oct: the whitelist itself includes the
 *  10 USDT test). The Worker's `payout_stage` names it, in the words of the wallet's db/0098 `custody_payout_stage()`: approved, test_sent,
 *  whitelisted — and `not_approved` before the owner's Books approval (and for any address no longer in use), which is NOT a stage: it falls to the
 *  approval words below ("Proposed" — the wallet screen's word for an address nobody has approved — or "Needs owner"). A stage this screen does not know is said as sent, never as another. A Worker that sends none (before db/0098) is read
 *  the old way: the owner's approval makes it "Approved"; before it, it says what it waits for. An address that is not IN USE — held, retired,
 *  archived — has no payout word at all: it cannot be paid, and its state pill says why. */
export const PAYOUT_STAGE_WORD: Record<string, { word: string; tone: PillTone }> = {
  approved:    { word: 'Approved',    tone: 'neutral' },
  test_sent:   { word: 'Test sent',   tone: 'warn' },
  whitelisted: { word: 'Whitelisted', tone: 'good' },
};

/** Payable by the wallet only when the OWNER approved it (the wallet's db/0092); a Books approval by anyone else is his to replace. */
export const ownerApproved = (w: StagedWallet): boolean => (w.payout_owner_approved ?? w.payout_approved_via === 'books') && !!w.payout_approved_at;

/** WAITING FOR HIS APPROVAL (his "propsed address gets hidden in the web of addresses", 4 Nov 2026): an address in use the desk
 *  may pay that the owner has not approved — what his Approve payout is for (a second person's Ledger-signed approval is its
 *  own path and offers no Approve). The register says them at its top, each a tap to its row. */
export const awaitsOwner = (w: StagedWallet): boolean =>
  approvesAsPayout(w) && w.status === 'active' && !ownerApproved(w) && w.payout_approved_via !== 'ledger_message';
export const waitingWords = (n: number): string => `${n} payout address${n === 1 ? '' : 'es'} waiting for approval`;

export function payoutFacts(w: StagedWallet): PayoutFacts | null {
  if (!approvesAsPayout(w) || w.status !== 'active') return null;
  const when = w.payout_approved_at ? fmtDay(w.payout_approved_at, true) : null;
  const sent = typeof w.payout_stage === 'string' ? w.payout_stage.trim() : '';
  const stage = sent && sent !== 'not_approved' ? sent : null;
  if (stage) {
    const known = Object.prototype.hasOwnProperty.call(PAYOUT_STAGE_WORD, stage) ? PAYOUT_STAGE_WORD[stage] : undefined;
    // A stage the Worker names is the Worker's word — but a positive one on a row whose own owner-approval fields say otherwise is a contradiction,
    // and the screen does not draw a green "Whitelisted" over an address it offers to approve: the approval words say what is true of the fields.
    if (!known) return { word: stage.replace(/_+/g, ' ').trim() || stage, tone: 'neutral', when, hint: null };
    if (ownerApproved(w)) return { word: known.word, tone: known.tone, when, hint: null };
  }
  if (ownerApproved(w)) return { word: 'Approved', tone: 'neutral', when, hint: null };
  return w.payout_approved_via === 'books'
    ? { word: 'Needs owner', tone: 'warn', when: null, hint: 'Needs the owner\u2019s approval' }
    : { word: 'Proposed', tone: 'warn', when: null, hint: 'Not approved yet' };
}

/** `more`: what follows from the note, said in the wallet's sheet and not on the row (Astra, 1 Nov 2026: a row's line is a few words). */
export interface RowNote { text: string; warn: boolean; more?: string }

/** The second line of a row: what the Books do with the address, said short — and said in amber only where an address in use is NOT read, so money
 *  sent to it would not be seen. (The sentences behind it are lib/walletStates.ts's, shown in the screen's About.) */
export function rowNote(w: RegisterWallet): RowNote | null {
  const retired = w.retired_at ? `Retired ${fmtDay(w.retired_at, true)}` : 'Retired';
  const read = chainIsRead(w.chain);
  // A desk address the chain reader does not read while the register says it is in use (or retired) is worth a line.
  const partyIdle = (w.status === 'active' || w.status === 'archived') && w.chain_read && !!w.treasury_slug && !w.watched;
  switch (w.status) {
    case 'active':
      if (!w.chain_read) return { text: `${w.chain} not read`, warn: true, more: 'Nothing sent here is seen.' };
      return partyIdle ? { text: 'Not read — its party is not active', warn: true } : null;
    case 'flagged':
      return { text: 'Held by screening', warn: false };
    case 'archived':
      return { text: `${retired} · ${!read ? `${w.chain} not read` : partyIdle ? 'not read, party not active' : 'still watched'}`, warn: false };
    case 'unwatched': {
      const again = w.retired_at && w.unwatched_at && w.retired_at.slice(0, 10) !== w.unwatched_at.slice(0, 10);
      return { text: `${again ? `${retired} · ` : ''}Archived${w.unwatched_at ? ` ${fmtDay(w.unwatched_at, true)}` : ''} · not watched`, warn: false };
    }
    default:
      return null;
  }
}

export interface RowFacts {
  name: string;
  state: { word: string; tone: PillTone };
  note: RowNote | null;
  payout: PayoutFacts | null;
  added: string;
  /** null = the chain reader has not seen it move. */
  seen: string | null;
  txs: number | null;
}

export function rowFacts(w: StagedWallet): RowFacts {
  const s = walletState(w.status);
  return {
    name: nameOf(w),
    state: { word: s.word, tone: s.tone },
    note: rowNote(w),
    payout: payoutFacts(w),
    added: fmtDay(w.first_seen_at, true),
    seen: w.last_seen_at ? fmtRelative(w.last_seen_at) : null,
    txs: w.chain_tx_count,
  };
}

// ── a row's steps ────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

export type Step = 'label' | 'retire' | 'archive' | 'approve';
export const STEP_WORD: Record<Step, string> = { label: 'Label', retire: 'Retire', archive: 'Archive', approve: 'Approve payout' };

export interface RowAction { step: Step; word: string; /** shown, and not pressable: only the owner approves payout addresses. */ disabled: boolean }

/** What a row offers, in the order it is offered. Nothing is offered to an operator who cannot change the register; an address that follows from a
 *  Ledger account key is the key's, never retired by hand. */
export function rowActions(w: StagedWallet, o: { canChange: boolean; canApprovePayouts: boolean }): RowAction[] {
  if (!o.canChange) return [];
  const out: RowAction[] = [{ step: 'label', word: STEP_WORD.label, disabled: false }];
  if (!w.from_ledger_key && (w.status === 'active' || w.status === 'flagged')) out.push({ step: 'retire', word: STEP_WORD.retire, disabled: false });
  if (!w.from_ledger_key && w.status !== 'unwatched') out.push({ step: 'archive', word: STEP_WORD.archive, disabled: false });
  if (approvesAsPayout(w) && w.status === 'active' && !ownerApproved(w) && w.payout_approved_via !== 'ledger_message') {
    out.push({ step: 'approve', word: STEP_WORD.approve, disabled: !o.canApprovePayouts });
  }
  return out;
}

/** Is a form's step still one this row offers? A row can change under an open form (another operator archived it, the owner approved it): the form stays — it
 *  holds a draft and maybe a request — but what the row no longer offers cannot be submitted. The owner-only rule is not asked here: whoever opened Approve
 *  was allowed to; this is about the ROW. */
export function stepStillOffered(w: StagedWallet, step: Step): boolean {
  return rowActions(w, { canChange: true, canApprovePayouts: true }).some((a) => a.step === step);
}

/** How many addresses in a section wait for an approval only the owner can give — the section says so ONCE when the one looking is not the owner,
 *  instead of every row saying it. */
export function approvalsWaiting(section: RegisterSection): number {
  let n = 0;
  for (const g of section.groups) for (const w of g.byHand) if (rowActions(w, { canChange: true, canApprovePayouts: true }).some((a) => a.step === 'approve')) n++;
  return n;
}

// ── a row's form ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** A row's open form: the step, what was typed, and the failure its last request answered. Held by the SCREEN, by address id, so it survives what
 *  unmounts a panel (a filter, the window crossing the laptop breakpoint) — a draft, a request in flight and its failure all live here. */
export interface Form {
  step: Step;
  reason: string;
  label: string;
  /** The label the draft began from — the row's own when the operator started typing. A draft still equal to it is UNTOUCHED (shownLabel follows the row);
   *  a typed draft over a row whose label has since moved is a conflict (labelConflict). */
  base: string;
  error: string | null;
}
export type Forms = Readonly<Record<string, Form>>;

/** Open `step` on a row. The row keeps what it had typed (one reason box serves retire and archive, as it always did); a row with no form yet starts
 *  with its label as it stands. Opening a step clears the failure of the last one. Each row keeps its OWN form: opening one leaves another's open. */
export function openForm(forms: Forms, w: Pick<RegisterWallet, 'id' | 'label'>, step: Step): Forms {
  const was = forms[w.id];
  const live = w.label ?? '';
  return { ...forms, [w.id]: { step, reason: was?.reason ?? '', label: was?.label ?? live, base: was?.base ?? live, error: null } };
}
/** What was typed (the reason, the label). A row with no open form is not made one. `live` is the row's label as it stands on screen: the first
 *  keystroke into an untouched label draft begins it FROM that label — not from the one the row had when the form was opened. */
export function editForm(forms: Forms, id: string, patch: Partial<Pick<Form, 'reason' | 'label'>>, live?: string): Forms {
  const was = forms[id];
  if (!was) return forms;
  const rebase = patch.label !== undefined && live !== undefined && was.label === was.base ? { base: live } : {};
  return { ...forms, [id]: { ...was, ...patch, ...rebase } };
}
/** The label a label form SHOWS and SAVES. An untouched draft follows the row — a refresh that brings another operator's label is what it then shows —
 *  and a draft someone typed in keeps what they typed. */
export function shownLabel(form: Pick<Form, 'label' | 'base'>, w: Pick<RegisterWallet, 'label'>): string {
  return form.label === form.base ? (w.label ?? '') : form.label;
}
/** A typed label over a row whose label has since changed (another operator's, read by a refresh): saving it would overwrite the newer label unseen — the
 *  BFF's label update is unconditional. The draft stays, the Save does not. */
export function labelConflict(form: Pick<Form, 'label' | 'base'>, w: Pick<RegisterWallet, 'label'>): boolean {
  return form.label !== form.base && (w.label ?? '') !== form.base;
}
/** Which open form owns the screen's ONE gold button: the one the operator is working in (the last opened or focused) while it is open, else the
 *  first open. `open` lists the open forms' keys — the add form as 'add', a row's by its address id. */
export function goldForm(open: readonly string[], last: string | null): string | null {
  if (last !== null && open.includes(last)) return last;
  return open[0] ?? null;
}
/** The open forms that CAN own the gold button, in the order goldForm falls back through them: the add form first, then the rows' by address id. An Archive form is
 *  the red destructive confirm — it never draws gold, so it never owns it (else opening it beside another form would leave the screen with none). */
export function goldCandidates(adding: boolean, forms: Forms): string[] {
  return [...(adding ? ['add'] : []), ...Object.keys(forms).filter((id) => forms[id]!.step !== 'archive')];
}
/** The failure a request answered, kept in the form it came from; a row whose form is not open keeps none. */
export function failForm(forms: Forms, id: string, words: string | null): Forms {
  const was = forms[id];
  return was ? { ...forms, [id]: { ...was, error: words } } : forms;
}
/** Close a row's form — only if it is still the step that was SUBMITTED (`step`; omit it to close whatever is open): an answer that arrives for a step
 *  that is no longer the open one closes nothing. */
export function closeForm(forms: Forms, id: string, step?: Step): Forms {
  const was = forms[id];
  if (!was || (step && was.step !== step)) return forms;
  const { [id]: _gone, ...rest } = forms;
  return rest;
}

// ── batch 5: ONE register (operator's ruling, 26 Oct 2026 — Astra's shape) ────────────────────────────────────────────────────────────────────

/** A row of the one register: an address registered by hand, or a party's Ledger account key standing for the addresses its scan keeps. */
export type RegisterRow =
  | { kind: 'wallet'; key: string; w: StagedWallet; role: string; group: RegisterGroup }
  | { kind: 'key'; key: string; group: RegisterGroup; role: string; rows: RegisterWallet[]; chains: string[] }
  /** Every per-transaction deposit address (class `deposit`) as ONE row (the operator, 28 Oct 2026, via the wallet session: they fold "the
   *  way Ledger keys already fold") — one is issued per deposit, so a busy week would bury the desk's own wallets under them. */
  | { kind: 'deposits'; key: 'deposits'; group: RegisterGroup; role: string; rows: RegisterWallet[]; chains: string[] };

/** The role a row says beside its owner: the desk's own, a payout address, or the party's kind. */
export function roleWord(section: SectionKey, g: Pick<RegisterGroup, 'type'>): string {
  if (section === 'desk') return 'Desk';
  if (section === 'payout') return 'Payout';
  const t = g.type.replace(/_/g, ' ');
  return t === 'counterparty' ? 'Merchant' : t.charAt(0).toUpperCase() + t.slice(1);
}

/** The sections flattened into ONE list, in their order — desk, payout, parties — each party's hand-registered addresses, then (when it has any)
 *  ONE row for its Ledger account key that opens the addresses the key's scan keeps. The per-transaction deposit addresses, wherever they stand,
 *  are ONE row "Deposit addresses", in the place the first of them would have had. Nothing is dropped: every row of every section is here. */
export function registerRows(sections: readonly RegisterSection[]): RegisterRow[] {
  const out: RegisterRow[] = [];
  const isDeposit = (w: RegisterWallet) => w.class === 'deposit';
  let deposits: Extract<RegisterRow, { kind: 'deposits' }> | null = null;
  const fold = (ws: readonly RegisterWallet[], g: RegisterGroup, role: string) => {
    if (ws.length === 0) return;
    if (!deposits) { deposits = { kind: 'deposits', key: 'deposits', group: g, role, rows: [], chains: [] }; out.push(deposits); }
    deposits.rows.push(...ws);
    deposits.chains = [...new Set(deposits.rows.map((w) => w.chain))];
  };
  for (const s of sections) {
    for (const g of s.groups) {
      const role = roleWord(s.key, g);
      for (const w of g.byHand) {
        if (isDeposit(w)) fold([w], g, role);
        else out.push({ kind: 'wallet', key: w.id, w, role, group: g });
      }
      fold(g.fromKey.filter(isDeposit), g, role);
      const keyed = g.fromKey.filter((w) => !isDeposit(w));
      if (keyed.length > 0) {
        const chains = [...new Set(keyed.map((w) => w.chain))];
        out.push({ kind: 'key', key: `key:${g.key}`, group: g, role, rows: keyed, chains });
      }
    }
  }
  return out;
}

/** What the register's foot says the list holds — the view's word and its count, the chain when one is chosen. Counted from the filters alone (a row
 *  kept for its open form is not "in use" because it is on screen). null over a read that is not known: a count over a failed refresh claims nothing. */
export function scopeWords(all: readonly RegisterWallet[], f: { show: Show; chain: string; search: string }, known: boolean): string | null {
  if (!known) return null;
  const n = filterRegister(all, f).length;
  const word = f.show === 'current' ? 'in use' : f.show === 'retired' ? 'retired' : f.show === 'archived' ? 'archived' : n === 1 ? 'address' : 'addresses';
  return `${n} ${word}${f.chain ? ` · ${f.chain}` : ''}${f.search.trim() ? ' · searched' : ''}`;
}

/** The chain-independent body of an address: the part a poisoner copies is its first and last characters after the chain's own prefix. */
const body = (a: string): string => a.trim().replace(/^0x/i, '').toLowerCase();

/** A LOOK-ALIKE: an address already in the register, on the same chain, that is NOT the typed one but shares its first four and last four characters —
 *  how an address-poisoning copy is made. Information, said once in red before the save (design system §5); it refuses nothing. */
export function lookalikeIn(all: readonly RegisterWallet[], chain: string, address: string): RegisterWallet | null {
  const t = body(address);
  if (t.length < 12) return null;
  for (const w of all) {
    if (w.chain !== chain) continue;
    const o = body(w.address);
    if (o === t || o.length < 12) continue;
    if (o.slice(0, 4) === t.slice(0, 4) && o.slice(-4) === t.slice(-4)) return w;
  }
  return null;
}

/** The same address already in the register on the same chain (the add door answers it with its own refusal; the review says it first). */
export function sameIn(all: readonly RegisterWallet[], chain: string, address: string): RegisterWallet | null {
  const t = body(address);
  return t ? all.find((w) => w.chain === chain && body(w.address) === t) ?? null : null;
}

/** A one-way step in ONE line, before it is taken (the operator's ruling: a safety check in its lightest form); the whole sentence — walletStates'
 *  stepWords — goes behind a tap. Said for the chain: on one the reader does not read, nothing is "still watched". */
export function stepLine(step: 'retire' | 'archive', chain: string): string {
  const read = chainIsRead(chain);
  if (step === 'retire') return read ? 'Still watched after. Cannot be undone.' : `${chain} is not read either way. Cannot be undone.`;
  return read ? 'Money sent here later goes unseen. Cannot be undone.' : 'Leaves the KYT and Energy screens. Cannot be undone.';
}
